// src/components/DatabaseExplorer/DynamicFilters.jsx

import React from "react";
import {
  getColumnName,
  getColumnType,
  getFilterType,
  getEnumOptions,
  formatHeader,
  isPrimaryKey,
} from "./databaseExplorerUtils";

function ColumnFilter({ column, value, rows, onChange }) {
  const name = getColumnName(column);
  const type = getFilterType(column);
  const sqlType = getColumnType(column);

  const update = (key, nextValue) => {
    onChange({
      ...value,
      [key]: nextValue,
    });
  };

  if (type === "number") {
    return (
      <div className="row g-2">
        <div className="col-6">
          <input
            type="number"
            className="form-control form-control-sm"
            placeholder="Minimum"
            value={value.min ?? ""}
            onChange={(event) => update("min", event.target.value)}
          />
        </div>

        <div className="col-6">
          <input
            type="number"
            className="form-control form-control-sm"
            placeholder="Maximum"
            value={value.max ?? ""}
            onChange={(event) => update("max", event.target.value)}
          />
        </div>
      </div>
    );
  }

  if (type === "date" || type === "datetime") {
    return (
      <div className="d-flex flex-column gap-2">
        <input
          type="date"
          className="form-control form-control-sm"
          aria-label={`${name} from`}
          value={value.from ?? ""}
          onChange={(event) => update("from", event.target.value)}
        />

        <input
          type="date"
          className="form-control form-control-sm"
          aria-label={`${name} to`}
          value={value.to ?? ""}
          onChange={(event) => update("to", event.target.value)}
        />
      </div>
    );
  }

  if (type === "boolean") {
    return (
      <select
        className="form-select form-select-sm"
        value={value.equals ?? ""}
        onChange={(event) => update("equals", event.target.value)}
      >
        <option value="">All values</option>
        <option value="1">True / 1</option>
        <option value="0">False / 0</option>
      </select>
    );
  }

  if (type === "enum") {
    return (
      <select
        className="form-select form-select-sm"
        value={value.equals ?? ""}
        onChange={(event) => update("equals", event.target.value)}
      >
        <option value="">All values</option>

        {getEnumOptions(sqlType).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  const suggestions = [
    ...new Set(
      rows
        .map((row) => row[name])
        .filter(
          (item) =>
            item !== null && item !== undefined && typeof item !== "object",
        )
        .map(String),
    ),
  ].slice(0, 100);

  return (
    <>
      <select
        className="form-select form-select-sm mb-2"
        value={value.operator || "contains"}
        onChange={(event) => update("operator", event.target.value)}
      >
        <option value="contains">Contains</option>
        <option value="equals">Equals</option>
        <option value="starts">Starts with</option>
        <option value="empty">Is empty</option>
        <option value="notEmpty">Is not empty</option>
      </select>

      {!["empty", "notEmpty"].includes(value.operator) && (
        <>
          <input
            className="form-control form-control-sm"
            list={`filter-${name}`}
            placeholder={`Filter ${formatHeader(name)}`}
            value={value.value ?? ""}
            onChange={(event) => update("value", event.target.value)}
          />

          <datalist id={`filter-${name}`}>
            {suggestions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
        </>
      )}
    </>
  );
}

export default function DynamicFilters({
  columns,
  rows,
  filters,
  onFilterChange,
  onClear,
}) {
  if (!columns.length) return null;

  return (
    <div className="border rounded-3 bg-light p-3 mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold mb-0">Filter by column</h6>

        <button
          type="button"
          className="btn btn-sm btn-link text-decoration-none"
          onClick={onClear}
        >
          Clear filters
        </button>
      </div>

      <div className="row g-3">
        {columns.map((column) => {
          const name = getColumnName(column);
          const subText = column.Type || column.type || "text";
          return (
            <div className="col-md-2" key={name}>
              <label className="form-label small fw-semibold mb-1">
                {formatHeader(name)}

                {isPrimaryKey(column) && (
                  <span className="badge text-bg-primary ms-1">PK</span>
                )}
                <span className="d-block text-secondary fw-normal">
                  {subText?.length > 20
                    ? `${subText.slice(0, 20)}...`
                    : subText}
                </span>
              </label>
              <ColumnFilter
                column={column}
                value={filters[name] || {}}
                rows={rows}
                onChange={(nextValue) => onFilterChange(name, nextValue)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
